import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Profile, Dispo } from '../types';

interface ProfilePageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [disponibilites, setDisponibilites] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [showDispoReminder, setShowDispoReminder] = useState(false);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await api.profiles.getByUserId(user!.id);
      
      if (error) {
        showToast('Erreur lors du chargement du profil', 'error');
        return;
      }

      if (data) {
        setProfile(data);
        
        // Charger les disponibilités depuis la table dispos séparée
        const { data: disposData, error: disposError } = await api.dispos.getByTuteurId(data.id);
        
        if (disposError) {
        } else if (disposData) {
          // Convertir les dispos en format pour l'affichage
          const disposByDay: Record<string, string[]> = {};
          disposData.forEach((dispo: Dispo) => {
            if (!disposByDay[dispo.jour]) {
              disposByDay[dispo.jour] = [];
            }
            disposByDay[dispo.jour].push(dispo.creneau);
          });
          setDisponibilites(disposByDay);
          
          // Afficher le rappel si l'utilisateur est tuteur et n'a pas de dispos
          if (data.role !== 'etudiant' && disposData.length === 0) {
            // Vérifier si l'utilisateur a déjà vu ce message
            const hasSeenDispoReminder = localStorage.getItem('hasSeenDispoReminder');
            if (!hasSeenDispoReminder) {
              setShowDispoReminder(true);
            }
          }
        } else {
          // Pas de données = pas de disponibilités
          if (data.role !== 'etudiant') {
            const hasSeenDispoReminder = localStorage.getItem('hasSeenDispoReminder');
            if (!hasSeenDispoReminder) {
              setShowDispoReminder(true);
            }
          }
        }
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (newRole: 'etudiant' | 'repetiteur' | 'les_deux') => {
    if (!profile) return;

    setChangingRole(true);
    try {
      const { data, error } = await api.profiles.updateRole(profile.id, newRole);
      
      if (error) {
        showToast('Erreur lors du changement de rôle', 'error');
        return;
      }

      if (data && data[0]) {
        setProfile(data[0]);
        setShowRoleModal(false);
        
        const roleNames = {
          'etudiant': 'étudiant-e',
          'repetiteur': 'tuteur-trice',
          'les_deux': 'étudiant-e et tuteur-trice'
        };
        
        showToast(`Rôle changé avec succès ! Vous êtes maintenant ${roleNames[newRole]}.`, 'success');
        
        // Recharger le profil pour s'assurer que toutes les données sont à jour
        await loadProfile();
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setChangingRole(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de votre profil...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mx-auto h-16 w-16 bg-yellow-600 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-user-plus text-white text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Aucun profil trouvé
            </h2>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore créé de profil tuteur. Créez-en un pour commencer à aider les étudiants.
            </p>
            <button
              onClick={() => navigate('/create-profile')}
              className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] transition-colors"
            >
              <i className="fas fa-plus mr-2"></i>
              Créer mon profil tuteur
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-user-cog text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mon Profil
          </h1>
          <p className="text-gray-600">
            Gérez vos informations et préférences
          </p>
        </div>

        {/* Notification : N'oubliez pas vos disponibilités */}
        {showDispoReminder && profile && profile.role !== 'etudiant' && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4 mb-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center">
                  <i className="fas fa-calendar-check text-white text-lg"></i>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className="flex items-center mb-1">
                  <h3 className="font-semibold text-green-900">
                    N'oubliez pas d'indiquer vos disponibilités !
                  </h3>
                  <span className="ml-2 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                </div>
                <p className="text-sm text-green-800 mb-3">
                  Pour que les étudiant-es puissent vous trouver facilement, pensez à renseigner vos créneaux horaires disponibles.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      localStorage.setItem('hasSeenDispoReminder', 'true');
                      setShowDispoReminder(false);
                      navigate('/profile/availability');
                    }}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <i className="fas fa-calendar-plus mr-2"></i>
                    Ajouter mes disponibilités
                  </button>
                  <button
                    onClick={() => {
                      localStorage.setItem('hasSeenDispoReminder', 'true');
                      setShowDispoReminder(false);
                    }}
                    className="px-4 py-2 bg-white text-green-700 text-sm font-semibold rounded-lg border border-green-300 hover:bg-green-50 transition-colors"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Menu de navigation */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-6">
            <button
              onClick={() => navigate('/profile/edit')}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-[#002c46]/5 transition-colors group"
            >
              <div className="h-12 w-12 bg-[#002c46]/10 rounded-full flex items-center justify-center mb-2 group-hover:bg-[#002c46]/20">
                <i className="fas fa-edit text-[#002c46] text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Modifier le profil</h3>
              <p className="text-sm text-gray-600 text-center">Informations personnelles</p>
            </button>

            <button
              onClick={() => setShowRoleModal(true)}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-[#002c46]/5 transition-colors group"
            >
              <div className="h-12 w-12 bg-[#002c46]/10 rounded-full flex items-center justify-center mb-2 group-hover:bg-[#002c46]/20">
                <i className="fas fa-exchange-alt text-[#002c46] text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Changer de rôle</h3>
              <p className="text-sm text-gray-600 text-center">Étudiant-e / Tuteur-trice</p>
            </button>

            {/* Boutons réservés aux tuteurs */}
            {profile && profile.role !== 'etudiant' && (
              <>
                <button
                  onClick={() => navigate('/profile/availability')}
                  className="flex flex-col items-center p-4 rounded-lg hover:bg-green-50 transition-colors group"
                >
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-green-200">
                    <i className="fas fa-calendar text-green-600 text-xl"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900">Disponibilités</h3>
                  <p className="text-sm text-gray-600 text-center">Jours et créneaux</p>
                </button>

                <button
                  onClick={() => navigate('/profile/subjects')}
                  className="flex flex-col items-center p-4 rounded-lg hover:bg-purple-50 transition-colors group"
                >
                  <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-purple-200">
                    <i className="fas fa-book text-purple-600 text-xl"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900">Matières</h3>
                  <p className="text-sm text-gray-600 text-center">Enseignements proposés</p>
                </button>
              </>
            )}

            <button
              onClick={() => navigate('/profile/requests')}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-orange-50 transition-colors group"
            >
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-orange-200">
                <i className="fas fa-envelope text-orange-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Demandes</h3>
              <p className="text-sm text-gray-600 text-center">Contact étudiants</p>
            </button>

            <button
              onClick={() => navigate('/profile/security')}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-red-50 transition-colors group"
            >
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-2 group-hover:bg-red-200">
                <i className="fas fa-shield-alt text-red-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Sécurité</h3>
              <p className="text-sm text-gray-600 text-center">Mot de passe</p>
            </button>
          </div>
        </div>

        {/* Aperçu du profil */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            <i className="fas fa-eye text-[#002c46] mr-2"></i>
            Aperçu de votre profil
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">Informations personnelles</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Nom:</span> {profile.prenom} {profile.nom}</p>
                <p><span className="font-medium">Email:</span> {user?.email}</p>
                <p><span className="font-medium">Filière:</span> {profile.filiere}</p>
                <p><span className="font-medium">Année:</span> {profile.annee}</p>
                <p><span className="font-medium">Langue:</span> {profile.langue === 'francais' ? '🇫🇷 Français' : '🇬🇧 Anglais'}</p>
                {profile.role !== 'etudiant' && (
                  <p><span className="font-medium">Tarif:</span> {profile.tarif} CHF/h</p>
                )}
              </div>
            </div>
            
            {/* Sections réservées aux tuteurs */}
            {profile.role !== 'etudiant' && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Matières enseignées</h3>
                <p className="text-gray-600 mb-4">{profile.matieres}</p>
                
                <h3 className="font-semibold text-gray-700 mb-3">Disponibilités</h3>
                <div className="space-y-1">
                  {Object.keys(disponibilites).length > 0 ? (
                    Object.entries(disponibilites).map(([jour, creneaux]) => (
                      <p key={jour} className="text-sm">
                        <span className="font-medium capitalize">{jour}:</span> {creneaux.join(', ')}
                      </p>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">Aucune disponibilité définie</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal de changement de rôle */}
        {showRoleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="mx-auto h-12 w-12 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
                  <i className="fas fa-exchange-alt text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Changer de rôle
                </h3>
                <p className="text-gray-600 text-sm">
                  Actuellement : <strong>
                    {profile?.role === 'etudiant' ? 'Étudiant-e' : 
                     profile?.role === 'repetiteur' ? 'Tuteur-trice' : 'Étudiant-e et Tuteur-trice'}
                  </strong>
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {['etudiant', 'repetiteur', 'les_deux'].map((role) => {
                  const isCurrentRole = profile?.role === role;
                  const roleInfo = {
                    'etudiant': { 
                      label: 'Étudiant-e', 
                      icon: 'fa-graduation-cap', 
                      color: 'blue',
                      description: 'Chercher de l\'aide dans mes études'
                    },
                    'repetiteur': { 
                      label: 'Tuteur-trice', 
                      icon: 'fa-chalkboard-teacher', 
                      color: 'green',
                      description: 'Aider d\'autres étudiant-es'
                    },
                    'les_deux': { 
                      label: 'Les deux', 
                      icon: 'fa-exchange-alt', 
                      color: 'purple',
                      description: 'À la fois étudiant-e et tuteur-trice'
                    }
                  };

                  return (
                    <button
                      key={role}
                      onClick={() => handleRoleChange(role as 'etudiant' | 'repetiteur' | 'les_deux')}
                      disabled={isCurrentRole || changingRole}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                        isCurrentRole 
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                          : `border-gray-200 hover:border-${roleInfo[role as keyof typeof roleInfo].color}-300 hover:bg-${roleInfo[role as keyof typeof roleInfo].color}-50`
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center mr-4 ${
                          isCurrentRole 
                            ? 'bg-gray-200' 
                            : `bg-${roleInfo[role as keyof typeof roleInfo].color}-100`
                        }`}>
                          <i className={`fas ${roleInfo[role as keyof typeof roleInfo].icon} ${
                            isCurrentRole 
                              ? 'text-gray-500' 
                              : `text-${roleInfo[role as keyof typeof roleInfo].color}-600`
                          }`}></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">
                            {roleInfo[role as keyof typeof roleInfo].label}
                            {isCurrentRole && (
                              <span className="ml-2 text-sm text-gray-500">(actuel)</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {roleInfo[role as keyof typeof roleInfo].description}
                          </p>
                        </div>
                        {!isCurrentRole && (
                          <i className="fas fa-arrow-right text-gray-400"></i>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRoleModal(false)}
                  disabled={changingRole}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
              </div>

              {changingRole && (
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center text-[#002c46]">
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Changement en cours...
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 